import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendMessage } from '@/hooks/useMessenger';
import { ComposerPlusPanel } from './ComposerPlusPanel';

interface ComposerProps {
  threadId: string;
}

export function Composer({ threadId }: ComposerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [plusOpen, setPlusOpen] = useState(false);
  const send = useSendMessage();

  function submit(e: FormEvent | KeyboardEvent) {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    send.mutate({ threadId, text: value });
    setText('');
    setPlusOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) submit(e);
  }

  return (
    <>
      <form className="composer" onSubmit={submit}>
        <input
          className="composer-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('messenger.composer_placeholder')}
          onFocus={() => setPlusOpen(false)}
        />
        {text.trim() ? (
          <button
            type="submit"
            className="composer-send"
            disabled={send.isPending}
            aria-label={t('messenger.send')}
          >
            <span className="material-symbols-outlined">send</span>
          </button>
        ) : (
          <button
            type="button"
            className={`composer-plus${plusOpen ? ' active' : ''}`}
            onClick={() => setPlusOpen((v) => !v)}
            aria-label={t('composer_plus.open')}
            aria-expanded={plusOpen}
          >
            <span className="material-symbols-outlined">{plusOpen ? 'close' : 'add'}</span>
          </button>
        )}
      </form>
      <ComposerPlusPanel threadId={threadId} open={plusOpen} onClose={() => setPlusOpen(false)} />
    </>
  );
}
